import React, {
  ComponentPropsWithRef,
  useCallback,
  useEffect,
  useState,
} from "react";

import { EmblaCarouselType } from "embla-carousel";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
} from "lucide-react";

import { Button } from "@/shared-web.ui/ui.button";
import { cn } from "@/shared-web.ui/util.cn";

type UsePrevNextButtonsType = {
  prevBtnDisabled: boolean;
  nextBtnDisabled: boolean;
  onPrevButtonClick: () => void;
  onNextButtonClick: () => void;
};

export const usePrevNextButtons = (
  emblaApi: EmblaCarouselType | undefined,
): UsePrevNextButtonsType => {
  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true);
  const [nextBtnDisabled, setNextBtnDisabled] = useState(true);

  const onPrevButtonClick = useCallback(() => {
    if (!emblaApi) {
      return;
    }
    emblaApi.scrollPrev();
  }, [emblaApi]);

  const onNextButtonClick = useCallback(() => {
    if (!emblaApi) {
      return;
    }
    emblaApi.scrollNext();
  }, [emblaApi]);

  const onSelect = useCallback((emApi: EmblaCarouselType) => {
    setPrevBtnDisabled(!emApi.canScrollPrev());
    setNextBtnDisabled(!emApi.canScrollNext());
  }, []);

  useEffect(() => {
    if (!emblaApi) {
      return;
    }

    onSelect(emblaApi);
    emblaApi.on("reInit", onSelect).on("select", onSelect);
  }, [emblaApi, onSelect]);

  return {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  };
};

interface ArrowButtonProps extends ComponentPropsWithRef<"button"> {
  arrow: "next" | "prev";
};

export const ArrowButton = (props: ArrowButtonProps) => {
  const { children, className, arrow, ...restProps } = props;

  return (
    <Button
      {...restProps}
      type="button"
      variant="outline"
      color="gray"
      size="1"
      className={cn(
        `
          size-8 rounded-full border-2 border-gray-7 flex
          items-center justify-center
        `,
        `
          text-gray-12
          disabled:text-gray-8
        `,
        className,
      )}
    >
      {
        arrow === "prev"
          ? <ChevronLeftIcon />
          : <ChevronRightIcon />
      }
    </Button>
  );
};
